import { useState, useRef, useEffect, useMemo } from "react";
import ArrowDown from '@Alpha/Images/arrow-down.svg?react';
import useOutsideClick from 'Hooks/useOutsideClick';

interface variantOption {
    value: string;
    label: string;
    outOfStock: boolean;
    quantity: number;
}

const VariantSelect = ({
    label,
    options,
    selected,
    baseId,
    returnFocus,
    onClickHandler
}: {
    label: string;
    options: variantOption[];
    selected: variantOption;
    baseId: string;
    returnFocus: boolean;
    onClickHandler: (articleNumber: string, quantity: number) => void;
}) => {
    const [showDropdown, toggleDropdown] = useState(false);
    const impersonationSelectRef = useRef<HTMLDivElement>(null);
    const [selectedIndex, setSelectedIndex] = useState(-1);

    const optionsRef = useRef([]);
    const firstRender = useRef(true);
    const sizeOrder = ['XXL', 'XL', 'L', 'M', 'S', 'XS'];

    useEffect(() => {
        if (firstRender.current && !returnFocus) {
            firstRender.current = false;
            return;
        }
        if (showDropdown) {
            optionsRef.current[selectedIndex]?.focus();
        } else {
            impersonationSelectRef.current?.focus();
        }
    }, [selectedIndex, showDropdown]);

    const impersonationDropdownRef = useOutsideClick(
        () => toggleDropdown(false),
        impersonationSelectRef
    );

    const sortedOptions = useMemo(() => {
        return [...options].sort((a, b) => {
            const indexA = sizeOrder.indexOf(a.label);
            const indexB = sizeOrder.indexOf(b.label);
        
            if (indexA !== -1 && indexB !== -1) {
                return indexA - indexB; 
            } else if (indexA !== -1) {
                return -1; 
            } else if (indexB !== -1) {
                return 1; 
            } else {
                return a.label.localeCompare(b.label); 
            }
        });
    }, [options]);
    
    const updateSelected = (option: variantOption) => {
        if(option.value != selected.value) {
            onClickHandler(option.value, option.quantity);
        }
        toggleDropdown(false);
    }

    const toggleSelect = (event: React.KeyboardEvent) => {
        if (event.code === 'Enter' || event.code === 'Space') {
            event.preventDefault();
            toggleDropdown(!showDropdown);
        }
        if (event.code === 'ArrowDown') {
            event.preventDefault();
            if(!showDropdown) {
                toggleDropdown(true);         
            }
            setSelectedIndex(0);
        }
        if (event.code === 'Escape') {
            event.preventDefault();
            toggleDropdown(false);
        }
        if (event.code === 'Tab') {
            toggleDropdown(false);
        }
    };

    const navigateSelect = (event: React.KeyboardEvent, option: variantOption) => {
        if (event.code === 'Enter') {
            event.preventDefault();
            updateSelected(option);
            toggleDropdown(false);
        }
        if (event.code === 'ArrowDown') {
            event.preventDefault();
            setSelectedIndex((prevIndex) =>
                Math.min(prevIndex + 1, options.length - 1)
              );
        }
        if (event.code === 'ArrowUp') {
            event.preventDefault();
            setSelectedIndex((prevIndex) => Math.max(prevIndex - 1, 0));
        }
        if (event.code === 'Escape' || event.code === 'Space') {
            event.preventDefault();
            toggleDropdown(false);
        }
        if (event.code === 'Tab') {
            toggleDropdown(false);
        }
    };
      
  return(
    <div className="mb-1 leading-4">
        <span className="text-xs mb-1">
            {label}
        </span>
        <div className="relative select-none">
            <div
                className="text-sm relative h-7 border py-1 pl-3.5 pr-8 cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap"
                onClick={() => toggleDropdown(!showDropdown)}
                onKeyDown={toggleSelect}
                ref={impersonationSelectRef}
                tabIndex={0}
            >
                {selected.label}
                <ArrowDown className={`absolute h-4 right-1 top-1.5 pointer-events-none ${
                    showDropdown &&
                    'rotate-[180deg]'
                }`}/>
            </div>
            {showDropdown && 
                <div
                className="absolute border rounded-sm bg-white min-w-full mt-1 text-sm z-10"
                ref={impersonationDropdownRef as React.MutableRefObject<HTMLDivElement>}
                >
                    {sortedOptions.map((option, index) => (
                        <div
                            key={`${option.value}-${baseId}`}
                            onClick={() => updateSelected(option)}
                            className={`min-h-9 border-b last:border-b-0 p-2 cursor-pointer hover:bg-slate-200 
                                ${option.value === selected.value ? 'bg-blue-100' : ''}
                                ${option.outOfStock ? 'line-through text-[#9D9FA6]' : ''}
                            `}
                            onKeyDown={(e) => navigateSelect(e, option)}
                            ref={(el) => (optionsRef.current[index] = el)}
                            tabIndex={-1}
                        >
                            {option.label}
                        </div>
                    ))}
                </div>
            }
        </div>
    </div>
)};

export default VariantSelect;
